import { FILTER_DEPENDENCIES } from '../constants/filterDependencies';
import type {
  Filter,
  FilterId,
  PrimarySelection,
  TreeFilter,
} from '../types/filters';
import type { FilterConsistencyState } from './filterGroupConsistency';
import { getFilterDefinition } from './filterRegistry';

export const processFilterDependencies = (
  currentState: FilterConsistencyState,
  triggerFilterId: FilterId,
  triggerFilterState: Filter
): FilterConsistencyState => {
  const dependencies = FILTER_DEPENDENCIES.get(triggerFilterId);
  if (!dependencies || !currentState.activeKey) {
    return currentState;
  }

  const currentActiveMap = currentState.groupMap.get(currentState.activeKey);
  if (!currentActiveMap) {
    return currentState;
  }

  const nextActiveMap = new Map(currentActiveMap);
  let mapChanged = false;

  // Track optional page-level primary selection updates
  let pageSelectionsUpdate: {
    filterId: FilterId;
    primarySelections: PrimarySelection[];
  } | null = null;

  for (const dep of dependencies) {
    const currentDependentFilterState = nextActiveMap.get(
      dep.dependentFilterId
    );
    const updatePayload = dep.transformer(
      triggerFilterState,
      currentDependentFilterState,
      nextActiveMap
    );

    if (updatePayload) {
      const existingDepFilter = nextActiveMap.get(dep.dependentFilterId);
      const updatedDepFilter = {
        ...(existingDepFilter || getFilterDefinition(dep.dependentFilterId)),
        ...updatePayload,
      } as Filter;

      nextActiveMap.set(dep.dependentFilterId, updatedDepFilter);
      mapChanged = true;

      // If this update includes primarySelections, remember to also update pageMap
      const tree = updatePayload as Partial<TreeFilter>;
      if (
        tree &&
        tree.primarySelections &&
        Array.isArray(tree.primarySelections) &&
        tree.primarySelections.length > 0
      ) {
        pageSelectionsUpdate = {
          filterId: dep.dependentFilterId,
          primarySelections: tree.primarySelections,
        };
      }
    }
  }

  if (mapChanged) {
    const newConsistencyMap = new Map(currentState.groupMap);
    newConsistencyMap.set(currentState.activeKey, nextActiveMap);

    // Optionally update pageMap to keep primary chips in sync
    if (pageSelectionsUpdate) {
      const newPageMap = new Map(currentState.pageMap);
      const activeKeyPrefix = `${currentState.activeKey}:`;
      for (const [pageKey, perPageMap] of newPageMap.entries()) {
        if (!pageKey.startsWith(activeKeyPrefix)) continue;
        const newPerPageMap = new Map(perPageMap);
        newPerPageMap.set(
          pageSelectionsUpdate.filterId,
          pageSelectionsUpdate.primarySelections
        );
        newPageMap.set(pageKey, newPerPageMap);
      }

      return {
        ...currentState,
        groupMap: newConsistencyMap,
        pageMap: newPageMap,
      };
    }

    return { ...currentState, groupMap: newConsistencyMap };
  }

  return currentState;
};
